import { FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { clsx } from "clsx";

import {
  AccountType,
  AssignAccountRequest,
  AssignAccountResponse,
  AssignmentEntityType,
} from "@/api/rest/models/account";
import { Banner } from "@/components/common/Banner";
import { sendToSentry } from "@/utils/sentry";
import { assignAccountToEntity } from "@/api/rest/services/accountService";

interface FormValues {
  type: AccountType;
  registryCode?: string;
  name?: string;
  email?: string;
  firstUserEmail?: string;
  firstUserName?: string;
}

export default function UserManagementForm({
  entityId,
  entityType,
  onUpdate,
  translationKeyPrefix,
  compact = false,
  showTopBorder = true,
}: {
  entityId: number;
  entityType: AssignmentEntityType;
  onUpdate: () => void | Promise<void>;
  translationKeyPrefix: string;
  compact?: boolean;
  showTopBorder?: boolean;
}) {
  const { t } = useTranslation();

  const [selectedType, setSelectedType] = useState<AccountType>(AccountType.INDIVIDUAL);
  const [formStatus, setFormStatus] = useState<{
    message: string | null;
    status: AssignAccountResponse | "EMPTY" | "ERROR" | "SUBMITTING";
  }>({
    message: null,
    status: "EMPTY",
  });

  useEffect(() => {
    if (formStatus.message && formStatus.status === AssignAccountResponse.SUCCESS) {
      const timer = setTimeout(() => setFormStatus({ message: null, status: formStatus.status }), 10000);
      return () => clearTimeout(timer);
    }
  }, [formStatus]);

  const submitForm = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus({ message: null, status: "SUBMITTING" });

    const formData = Object.fromEntries(new FormData(e.currentTarget).entries()) as unknown as FormValues;

    if (formData.type === AccountType.INDIVIDUAL) {
      if (!formData.email) {
        setFormStatus({
          message: t("settings.emailRequired"),
          status: "ERROR",
        });
        return;
      }
    } else if (formData.type === AccountType.ORGANIZATION) {
      if (!formData.registryCode) {
        setFormStatus({
          message: t("settings.registryCodeRequired"),
          status: "ERROR",
        });
        return;
      }
    }

    try {
      const data: AssignAccountRequest = {
        type: formData.type,
        individualAccount:
          formData.type === AccountType.INDIVIDUAL
            ? {
                email: formData.email!,
                name: formData.name,
              }
            : undefined,
        organizationAccount:
          formData.type === AccountType.ORGANIZATION
            ? {
                registryCode: formData.registryCode!,
                name: formData.name,
                firstUserEmail: formData.firstUserEmail,
                firstUserName: formData.firstUserName,
              }
            : undefined,
      };

      const response = await assignAccountToEntity(entityType, entityId, data);

      if (response.result === AssignAccountResponse.MORE_INFO_REQUIRED) {
        setFormStatus({
          status: AssignAccountResponse.MORE_INFO_REQUIRED,
          message: t("settings.moreInfoRequired"),
        });
      } else if (response.result === AssignAccountResponse.SUCCESS) {
        setFormStatus({
          status: AssignAccountResponse.SUCCESS,
          message: t("settings.saveSuccess"),
        });
        await onUpdate();
      } else {
        setFormStatus({
          status: "ERROR",
          message: t("settings.saveFailed"),
        });
      }
    } catch (e) {
      sendToSentry(e as Error, {
        additionalContext: { context: "UserManagementForm.submitForm" },
      });

      setFormStatus({
        status: "ERROR",
        message: e instanceof Error ? e.message : t("settings.saveFailed"),
      });
    }
  };

  const labelClassName = compact ? "text-xs font-medium text-gray-500" : "text-gray-500";
  const inputClassName = compact
    ? "h-9 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-dobu-orange focus:outline-none focus:ring-1 focus:ring-dobu-orange"
    : "border border-gray-300 rounded-md p-2 w-full";

  return (
    <form
      className={clsx(
        "flex flex-col",
        compact ? "gap-3" : "gap-0",
        compact && showTopBorder && "border-t border-gray-200 pt-3",
      )}
      onSubmit={submitForm}
    >
      <span className={compact ? "text-sm font-semibold text-gray-900" : "text-xl font-bold pb-4"}>
        {t(`${translationKeyPrefix}.addNewAccess`)}
      </span>
      <div className={clsx("flex flex-row gap-4 flex-wrap md:flex-nowrap", !compact && "pb-4")}>
        <div className="flex flex-col flex-grow basis-full md:basis-1/2">
          <label className={labelClassName} htmlFor="type">
            {t("settings.type")}
          </label>
          <select
            className={inputClassName}
            name="type"
            required
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value as AccountType)}
          >
            <option value={AccountType.INDIVIDUAL}>{t("settings.individual")}</option>
            <option value={AccountType.ORGANIZATION}>{t("settings.organization")}</option>
          </select>
        </div>
        <div className="flex flex-col flex-grow basis-full md:basis-1/2">
          {selectedType === AccountType.INDIVIDUAL ? (
            <>
              <label className={labelClassName} htmlFor="email">
                {t("settings.email")}
              </label>
              <input className={inputClassName} name="email" type="email" placeholder={t("settings.email")} required />
            </>
          ) : (
            <>
              <label className={labelClassName} htmlFor="registryCode">
                {t("settings.registryCode")}
              </label>
              <input
                className={inputClassName}
                name="registryCode"
                type="number"
                placeholder={t("settings.registryCode")}
                required
              />
            </>
          )}
        </div>
      </div>

      {formStatus.status === AssignAccountResponse.MORE_INFO_REQUIRED && (
        <div className={clsx("flex flex-row gap-4 flex-wrap md:flex-nowrap", !compact && "pb-4")}>
          {selectedType === AccountType.INDIVIDUAL ? (
            <div className="flex flex-col flex-grow basis-full md:basis-1/2">
              <label className={labelClassName} htmlFor="name">
                {t("settings.personName")}
              </label>
              <input
                className={inputClassName}
                name="name"
                type="text"
                placeholder={t("settings.personName")}
                required
              />
            </div>
          ) : (
            <>
              <div className="flex flex-col flex-grow basis-full md:basis-1/3">
                <label className={labelClassName} htmlFor="name">
                  {t("settings.companyName")}
                </label>
                <input
                  className={inputClassName}
                  name="name"
                  type="text"
                  placeholder={t("settings.companyName")}
                  required
                />
              </div>
              <div className="flex flex-col flex-grow basis-full md:basis-1/3">
                <label className={labelClassName} htmlFor="firstUserEmail">
                  {t("settings.companyFirstUserEmail")}
                </label>
                <input
                  className={inputClassName}
                  name="firstUserEmail"
                  type="email"
                  placeholder={t("settings.companyFirstUserEmail")}
                  required
                />
              </div>
              <div className="flex flex-col flex-grow basis-full md:basis-1/3">
                <label className={labelClassName} htmlFor="firstUserName">
                  {t("settings.companyFirstUserName")}
                </label>
                <input
                  className={inputClassName}
                  name="firstUserName"
                  type="text"
                  placeholder={t("settings.companyFirstUserName")}
                  required
                />
              </div>
            </>
          )}
        </div>
      )}

      {formStatus.message && (
        <div className="mb-4">
          <Banner message={formStatus.message} success={formStatus.status !== "ERROR"} />
        </div>
      )}

      <button
        className={clsx(
          "btn-custom rounded-md bg-dobu-orange text-white hover:bg-dobu-orange-focus disabled:bg-gray-300 disabled:text-gray-500",
          compact ? "self-start px-4 py-1.5 text-sm font-medium" : "flex-grow p-2",
        )}
        disabled={formStatus.status === "SUBMITTING"}
      >
        {formStatus.status === "SUBMITTING" ? t("common.saving") : t("settings.save")}
      </button>
    </form>
  );
}
